import { AlertTriangle, ArrowDownLeft, ArrowUpRight, CreditCard, Wallet as WalletIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Skeleton,
  StatCard,
} from '@/components/ui';
import { useI18n, type TranslationKey } from '@/i18n';
import { useNotify } from '@/hooks/useNotify';
import { api, type BillingOverview, type LedgerEntry, type LedgerType } from '@/lib/api';
import { formatDateTime, formatPrice } from '@/lib/format';

/** Vorschlaege fuer die Aufladung in Cent. */
const PRESETS = [1000, 2500, 5000, 10_000] as const;

const TYPE_LABEL = {
  TOPUP: 'billing.typeTOPUP',
  CHARGE: 'billing.typeCHARGE',
  REFUND: 'billing.typeREFUND',
  ADJUSTMENT: 'billing.typeADJUSTMENT',
} as const satisfies Record<LedgerType, TranslationKey>;

export function BillingPage() {
  const { t } = useI18n();
  const notify = useNotify();
  const [params, setParams] = useSearchParams();

  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [amountCents, setAmountCents] = useState<number>(PRESETS[0]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [data, history] = await Promise.all([api.billing.overview(), api.billing.entries()]);
      setOverview(data);
      setEntries(history.entries);
    } catch (cause) {
      notify.fromError(cause, t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // --- Rueckkehr von PayPal ------------------------------------------------
  // Der Webhook bucht unabhaengig davon; dieser Aufruf sorgt nur dafuer, dass
  // der Kunde sein Guthaben sofort sieht statt erst nach dem Webhook.
  const handled = useRef(false);
  useEffect(() => {
    const outcome = params.get('paypal');
    const orderId = params.get('token');
    if (!outcome || handled.current) return;
    handled.current = true;

    const clear = () => {
      const next = new URLSearchParams(params);
      for (const key of ['paypal', 'token', 'PayerID']) next.delete(key);
      setParams(next, { replace: true });
    };

    if (outcome === 'abbruch' || !orderId) {
      if (orderId) void api.billing.cancelTopup(orderId).catch(() => undefined);
      notify.info(t('billing.topUpCancelled'));
      clear();
      return;
    }

    void (async () => {
      try {
        const result = await api.billing.captureTopup(orderId);
        notify.success(
          result.credited
            ? t('billing.topUpSuccess', { amount: formatPrice(result.amountCents) })
            : t('billing.topUpAlreadyCredited'),
        );
        await load();
      } catch (cause) {
        notify.fromError(cause, t('errors.generic'));
      } finally {
        clear();
      }
    })();
  }, [params, setParams, notify, t, load]);

  const startTopUp = async () => {
    setBusy(true);
    try {
      const order = await api.billing.createTopup(amountCents);
      notify.info(t('billing.topUpRedirect'));
      window.location.href = order.approveUrl;
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
      setBusy(false);
    }
  };

  const coverage = useMemo(() => {
    if (!overview || overview.monthlyTotalCents <= 0) return null;
    return Math.floor(overview.wallet.availableCents / overview.monthlyTotalCents);
  }, [overview]);

  if (loading) {
    return (
      <div>
        <PageHeader title={t('billing.title')} subtitle={t('billing.subtitle')} />
        <div className="grid gap-4 sm:grid-cols-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  if (!overview) return null;

  const { wallet, topup } = overview;
  const isInvoiceMode = overview.billingMode === 'INVOICE';
  const negative = wallet.balanceCents < 0;
  const low = !isInvoiceMode && wallet.availableCents < overview.monthlyTotalCents;

  return (
    <div>
      <PageHeader title={t('billing.title')} subtitle={t('billing.subtitle')} />

      {(negative || low) && !isInvoiceMode && (
        <div className="mb-4 flex items-start gap-3 rounded-[var(--radius-vela-md)] border border-warn/30 bg-warn-soft px-4 py-3">
          <AlertTriangle size={17} className="mt-0.5 shrink-0 text-warn" />
          <p className="text-[13px] leading-relaxed text-t0">
            {negative ? t('billing.negativeBalance') : t('billing.lowBalanceWarning')}
          </p>
        </div>
      )}

      {isInvoiceMode && (
        <div className="mb-4 rounded-[var(--radius-vela-md)] border border-line bg-bg-2 px-4 py-3">
          <p className="text-[13px] text-t1">{t('billing.invoiceModeHint')}</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label={t('billing.balance')}
          value={formatPrice(wallet.balanceCents, wallet.currency)}
          icon={<WalletIcon size={18} />}
          iconColor={negative ? 'var(--bad)' : 'var(--acc)'}
          iconBg={negative ? 'var(--bad-soft)' : 'var(--acc-soft)'}
        />
        <StatCard
          label={t('billing.available')}
          value={formatPrice(wallet.availableCents, wallet.currency)}
          icon={<CreditCard size={18} />}
          iconColor="var(--info)"
          iconBg="var(--info-soft)"
        />
        <StatCard
          label={t('billing.monthlyTotal')}
          value={formatPrice(overview.monthlyTotalCents, wallet.currency)}
          icon={<ArrowDownLeft size={18} />}
          iconColor="var(--warn)"
          iconBg="var(--warn-soft)"
        />
      </div>

      <p className="mt-2 text-[12px] text-t2">
        {wallet.creditLimitCents > 0
          ? `${t('billing.creditLimit')}: ${formatPrice(wallet.creditLimitCents, wallet.currency)} · ${t('billing.availableHint')}`
          : t('billing.noCreditLimit')}
        {coverage !== null &&
          ` · ${coverage >= 1 ? t('billing.coversMonths', { months: coverage }) : t('billing.coversLessThanMonth')}`}
      </p>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,340px)_1fr]">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>{t('billing.topUp')}</CardTitle>
              <CardSubtitle>
                {topup.provider
                  ? t('billing.topUpRange', {
                      min: formatPrice(topup.minCents, topup.currency),
                      max: formatPrice(topup.maxCents, topup.currency),
                    })
                  : t('billing.topUpUnavailableHint')}
              </CardSubtitle>
            </div>
          </CardHeader>

          {topup.provider ? (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                {PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setAmountCents(preset)}
                    className={`h-9 rounded-[var(--radius-vela-sm)] border text-xs font-semibold transition ${
                      amountCents === preset
                        ? 'border-acc bg-acc-soft text-acc'
                        : 'border-line bg-bg-3 text-t1 hover:border-line-2'
                    }`}
                  >
                    {formatPrice(preset, topup.currency)}
                  </button>
                ))}
              </div>

              <FormField label={t('billing.topUpAmount')}>
                <Input
                  type="number"
                  min={topup.minCents / 100}
                  max={topup.maxCents / 100}
                  step="0.01"
                  value={(amountCents / 100).toFixed(2)}
                  onChange={(event) =>
                    setAmountCents(Math.round(Number.parseFloat(event.target.value || '0') * 100))
                  }
                />
              </FormField>

              <Button
                fullWidth
                onClick={() => void startTopUp()}
                disabled={
                  busy || amountCents < topup.minCents || amountCents > topup.maxCents
                }
                icon={<CreditCard size={15} />}
              >
                {t('billing.topUpWithPayPal')}
              </Button>
            </div>
          ) : (
            <p className="text-[13px] text-t2">{t('billing.topUpUnavailable')}</p>
          )}
        </Card>

        <Card padding="none">
          <div className="border-b border-line px-5 py-4">
            <CardTitle>{t('billing.history')}</CardTitle>
          </div>

          {entries.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon="💶"
                title={t('billing.historyEmpty')}
                description={t('billing.historyEmptyText')}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-t2">
                    <th className="px-5 py-2.5 font-semibold">{t('billing.date')}</th>
                    <th className="px-5 py-2.5 font-semibold">{t('billing.description')}</th>
                    <th className="px-5 py-2.5 text-right font-semibold">{t('billing.amount')}</th>
                    <th className="px-5 py-2.5 text-right font-semibold">
                      {t('billing.balanceAfter')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {entries.map((entry) => (
                    <LedgerRow key={entry.id} entry={entry} currency={wallet.currency} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function LedgerRow({ entry, currency }: { entry: LedgerEntry; currency: string }) {
  const { t } = useI18n();
  const credit = entry.amountCents > 0;

  return (
    <tr>
      <td className="whitespace-nowrap px-5 py-3 text-t2">{formatDateTime(entry.createdAt)}</td>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          {credit ? (
            <ArrowUpRight size={14} className="shrink-0 text-ok" />
          ) : (
            <ArrowDownLeft size={14} className="shrink-0 text-t2" />
          )}
          <span className="text-t0">{entry.description}</span>
          <Badge variant={credit ? 'success' : 'neutral'}>{t(TYPE_LABEL[entry.type])}</Badge>
        </div>
        {entry.note && <p className="mt-1 pl-6 text-[12px] text-t2">{entry.note}</p>}
      </td>
      <td
        className={`whitespace-nowrap px-5 py-3 text-right font-semibold tabular-nums ${
          credit ? 'text-ok' : 'text-t0'
        }`}
      >
        {credit ? '+' : ''}
        {formatPrice(entry.amountCents, currency)}
      </td>
      <td className="whitespace-nowrap px-5 py-3 text-right tabular-nums text-t1">
        {formatPrice(entry.balanceAfterCents, currency)}
      </td>
    </tr>
  );
}
